/*
 * SPDX-FileCopyrightText: 2026 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */

/** The view modes a note can be opened in: editor, slideshow or read-only document */
export type NoteViewMode = 'n' | 'p' | 's'

/**
 * Encodes a note alias as a single path segment for API requests.
 * Aliases can contain slashes, which must not be interpreted as path separators by the backend.
 *
 * @param alias The alias to encode
 * @return The encoded alias
 */
export const encodeAliasForApi = (alias: string): string => encodeURIComponent(alias)

/**
 * Builds the relative url of a note. The slashes of nested aliases are kept, so that the url mirrors the
 * location of the note, e.g. `/n/incidents/2026/database-outage`.
 *
 * @param alias The alias of the note
 * @param mode The view mode to open the note in
 * @return The url of the note
 */
export const buildNoteUrl = (alias: string, mode: NoteViewMode = 'n'): string =>
  `/${mode}/${alias.split('/').map(encodeURIComponent).join('/')}`

const safeDecode = (segment: string): string => {
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment
  }
}

/**
 * Extracts the note alias from a catch-all route parameter.
 *
 * @param param The route parameter, which contains one entry per path segment
 * @return The alias or undefined if the parameter is empty
 */
export const aliasFromRouteParam = (param: string | string[] | undefined): string | undefined => {
  if (param === undefined) {
    return undefined
  }
  const segments = (Array.isArray(param) ? param : [param]).map(safeDecode).filter((segment) => segment !== '')
  return segments.length === 0 ? undefined : segments.join('/')
}
